import { Button, Popconfirm, Spin } from "antd";
import dayjs from "dayjs";
import { ArrowRight, Check, Zap } from "lucide-react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useGetSubscriptions } from "../../api/api.get";
import { plans } from "../../utils/constants";
import { useDeleteSubscription } from "../../api/api.delete";
import { useNotifications } from "../../components/notifications/notification";

export default function SubscriptionsPage() {
  const navigate = useNavigate();

  const { mutateAsync } = useDeleteSubscription();

  const { data: subscriptionsData, isLoading, refetch } = useGetSubscriptions();

  const currentPlan = useMemo(() => {
    if (subscriptionsData && subscriptionsData.length > 0) {
      return plans.find((p) => p.id === subscriptionsData[0].entitlement);
    }
    return undefined;
  }, [subscriptionsData]);

  const { notification, notifySuccess, notifyError } = useNotifications();

  const handleDelete = () => {
    mutateAsync()
      .then(() => {
        notifySuccess({
          title: "Thank you.",
          description: "Subscription is cancelled",
        });
        refetch();
      })
      .catch(() => notifyError("Whoops! Please email help@expensehut.com"));
  };

  const upgradeables = useMemo(() => {
    if (currentPlan) {
      // filter out the existing plan.
      // Easy way is to find entitlements higher than current
      const upgrades = plans.filter((p) => p.id > currentPlan.id);
      return upgrades;
    }
    return plans;
  }, [currentPlan]);

  return (
    <div className="min-h-screen mx-auto max-w-5xl">
      {notification}

      <section className="pt-12 pb-2 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-7xl lg:text-5xl font-bold text-foreground mb-6 leading-tight">
            Simple, Transparent
            <span className="text-purple-500"> Pricing</span>
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto leading-relaxed">
            Upgrade to automatically sync transactions from your credit card or
            bank account every day.
          </p>
        </div>
      </section>

      {isLoading && (
        <div className="flex justify-center align-center">
          <Spin size="large" />
        </div>
      )}

      {!isLoading && (
        <>
          {currentPlan && subscriptionsData && (
            <section className="py-10 px-4 sm:px-6 mb-10 lg:px-8 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-slate-200">
              <div className="">
                <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-md">
                  <h2 className="text-2xl font-bold text-foreground mb-2 flex gap-4 items-center">
                    <img
                      src="/assets/icons/credit-card.svg"
                      width={40}
                      alt="subscription-image"
                    />
                    {currentPlan.name}
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    This is your current subscription plan.
                  </p>

                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-3">
                      <span className="text-sm font-semibold">
                        Net: ${subscriptionsData[0].amount}
                      </span>
                      <span className="text-sm font-semibold">
                        Tax: {subscriptionsData[0].tax ?? 0}%
                      </span>
                      <span className="text-sm font-semibold">
                        Total: ${subscriptionsData[0].total}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <p className="text-sm text-muted-foreground mb-1">
                        Billing Cycle Date:{" "}
                        <strong>
                          {dayjs(subscriptionsData[0].monthlyCycleDate).format(
                            "Do",
                          )}{" "}
                          day of month
                        </strong>
                      </p>

                      <p className="text-xs text-muted-foreground mb-1">
                        Subscribed On:{" "}
                        {dayjs(subscriptionsData[0].createdAt).format(
                          "DD MMM YYYY",
                        )}{" "}
                      </p>
                    </div>

                    <div className="flex justify-end">
                      <Popconfirm
                        title="Are you sure you want to cancel?"
                        onConfirm={() => handleDelete()}
                      >
                        <Button type="link" danger>
                          Cancel subscription
                        </Button>
                      </Popconfirm>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {!subscriptionsData && (
            <p className="text-red-400 flex justify-center text-center m-4 mb-8 border-red-500 border bg-red-50 rounded-lg">
              Unable to find subscriptions data. Please email{" "}
              <strong>help@expensehut.com</strong>
            </p>
          )}
        </>
      )}

      <section>
        {/* Pricing Cards Section */}
        <section className="py-0 px-4 sm:px-6 lg:px-8 bg-slate-50">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              {upgradeables.map((plan, idx) => (
                <div
                  className="relative group h-full cursor-pointer"
                  onClick={() => navigate(`/subscriptions/${plan.id}/create`)}
                  key={idx}
                >
                  <div
                    className={`relative flex justify-center ${plan.badge ? "top-[20px]" : "mt-3"} z-[199] px-8`}
                  >
                    <span
                      className={`inline-block px-4 py-2.5 ${plan.badgeColor} text-white text-xs font-bold rounded-full shadow-lg`}
                    >
                      {plan.badge}
                    </span>
                  </div>

                  {/* Highlight ring for best value */}

                  <div className="relative bg-white rounded-3xl border-2 border-emerald-200 shadow-xl hover:shadow-2xl transition duration-300 overflow-hidden h-full flex flex-col">
                    {/* Gradient Header */}
                    <div className={`${plan.bgColor} px-8 pt-8 pb-6`}>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center text-white">
                            <Zap className="w-6 h-6" />
                          </div>
                          <h3 className="text-2xl font-bold text-white">
                            {plan.name}
                          </h3>
                        </div>
                      </div>
                      <p className="text-white text-sm">{plan.description}</p>
                    </div>

                    <div className="px-8 pt-6">
                      <div className="flex items-baseline gap-2 mb-2">
                        <span className="text-5xl font-bold text-foreground">
                          ${plan.price}
                        </span>
                        <span className="text-muted-foreground text-sm">
                          {plan.period}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-6">
                        Billed monthly. Cancel anytime.
                      </p>
                    </div>

                    <div className="px-8 mb-8">
                      <span
                        className={`w-full py-3 ${plan.buttonColor} text-white font-semibold rounded-xl transition flex items-center justify-center gap-2 shadow-lg hover:shadow-xl`}
                      >
                        Upgrade <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>

                    {/* Features */}
                    <div className="px-8 pb-8 flex-1">
                      <p className="text-xs font-semibold text-muted-foreground mb-4 uppercase tracking-wide">
                        What's Included
                      </p>
                      <ul className="space-y-3">
                        {plan.features.map((feature: string, idx: number) => (
                          <li key={idx} className="flex items-start gap-3">
                            <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-foreground">
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold text-center text-foreground mb-16">
              Frequently Asked Questions
            </h2>

            <div className="space-y-8">
              <div className="border-b border-slate-200 pb-8">
                <h3 className="text-lg font-bold text-foreground mb-3">
                  Can I switch plans anytime?
                </h3>
                <p className="text-muted-foreground">
                  Yes! You can upgrade or downgrade your plan at any time.
                  Changes take effect immediately on your next billing cycle.
                </p>
              </div>

              <div className="border-b border-slate-200 pb-8">
                <h3 className="text-lg font-bold text-foreground mb-3">
                  What payment methods do you accept?
                </h3>
                <p className="text-muted-foreground">
                  We accept all major credit cards and debit cards.
                </p>
              </div>

              <div className="border-b border-slate-200 pb-8">
                <h3 className="text-lg font-bold text-foreground mb-3">
                  Is there a free plan?
                </h3>
                <p className="text-muted-foreground">
                  ExpenseTracker is free by default without paid features.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-bold text-foreground mb-3">
                  Can I cancel anytime?
                </h3>
                <p className="text-muted-foreground">
                  Yes. you can cancel your subscription anytime. Please see
                  refund policy below.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-bold text-foreground mb-3">
                  What is your refund policy?
                </h3>
                <p className="text-muted-foreground">
                  We do not refund any of the subscriptions fees. Your account
                  will automatically switch to free tier after cancellation
                  period.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-bold text-foreground mb-3">
                  How do I contact support?
                </h3>
                <p className="text-muted-foreground">
                  Please email <strong>help@expensehut.com</strong>
                </p>
              </div>
            </div>
          </div>
        </section>
      </section>
    </div>
  );
}
